import { createAsyncThunk, createEntityAdapter, createSlice } from '@reduxjs/toolkit'
import { apiStatus } from 'shared/constants/AppEnum'
import { AppState } from 'store/reduxStore'
import { IApiState } from 'store/types'
import { whatsappPackageApi, whatsappTemplateApi, whatsappWalletApi } from 'api/whatsapp'
import { whatsappAdminPackageApi, whatsappAdminTemplateApi } from 'api/whatsapp/admin'
import {
    WhatsAppPackageType,
    WhatsAppTemplateType,
    WhatsAppWalletType,
    WhatsAppTransactionType,
    WhatsAppRechargeType,
    AdminPackageRequest,
    AdminTemplateRequest,
    AdminTemplateListParams,
} from 'api/whatsapp/types'

// Entity adapters
const transactionAdapter = createEntityAdapter<WhatsAppTransactionType>()
const rechargeAdapter = createEntityAdapter<WhatsAppRechargeType>()
const templateAdapter = createEntityAdapter<WhatsAppTemplateType>()
const packageAdapter = createEntityAdapter<WhatsAppPackageType>()
const adminPackageAdapter = createEntityAdapter<WhatsAppPackageType>()
const adminTemplateAdapter = createEntityAdapter<WhatsAppTemplateType>()

type TempleIdPayload = { templeId?: string | number }

type WhatsAppStateType = {
    // TAdmin (temple-scoped)
    wallet: WhatsAppWalletType | null
    walletApi: IApiState
    transactions: ReturnType<typeof transactionAdapter.getInitialState>
    transactionsApi: IApiState
    recharges: ReturnType<typeof rechargeAdapter.getInitialState>
    rechargesApi: IApiState
    rechargeInitiateApi: IApiState
    templates: ReturnType<typeof templateAdapter.getInitialState>
    templatesApi: IApiState
    packages: ReturnType<typeof packageAdapter.getInitialState>
    packagesApi: IApiState
    // Admin (global)
    adminPackages: ReturnType<typeof adminPackageAdapter.getInitialState>
    adminPackagesApi: IApiState
    adminPackageCreateApi: IApiState
    adminPackageUpdateApi: IApiState
    adminPackageDeleteApi: IApiState
    adminTemplates: ReturnType<typeof adminTemplateAdapter.getInitialState>
    adminTemplatesApi: IApiState
    adminTemplateCreateApi: IApiState
    adminTemplateUpdateApi: IApiState
    adminTemplateDeleteApi: IApiState
}

const initialState: WhatsAppStateType = {
    wallet: null,
    walletApi: { status: apiStatus.IDLE, error: null },
    transactions: transactionAdapter.getInitialState(),
    transactionsApi: { status: apiStatus.IDLE, error: null },
    recharges: rechargeAdapter.getInitialState(),
    rechargesApi: { status: apiStatus.IDLE, error: null },
    rechargeInitiateApi: { status: apiStatus.IDLE, error: null },
    templates: templateAdapter.getInitialState(),
    templatesApi: { status: apiStatus.IDLE, error: null },
    packages: packageAdapter.getInitialState(),
    packagesApi: { status: apiStatus.IDLE, error: null },
    adminPackages: adminPackageAdapter.getInitialState(),
    adminPackagesApi: { status: apiStatus.IDLE, error: null },
    adminPackageCreateApi: { status: apiStatus.IDLE, error: null },
    adminPackageUpdateApi: { status: apiStatus.IDLE, error: null },
    adminPackageDeleteApi: { status: apiStatus.IDLE, error: null },
    adminTemplates: adminTemplateAdapter.getInitialState(),
    adminTemplatesApi: { status: apiStatus.IDLE, error: null },
    adminTemplateCreateApi: { status: apiStatus.IDLE, error: null },
    adminTemplateUpdateApi: { status: apiStatus.IDLE, error: null },
    adminTemplateDeleteApi: { status: apiStatus.IDLE, error: null },
}

// ─── TAdmin Thunks (temple-scoped) ───────────────────────────────────────────

export const fetchWalletThunk = createAsyncThunk(
    'whatsapp/wallet/fetch',
    async (payload: TempleIdPayload, { rejectWithValue }) => {
        try {
            const response = await whatsappWalletApi.getWallet(payload.templeId)
            return response.data
        } catch (err: any) {
            return rejectWithValue(err?.response?.data ?? { detail: err?.message ?? 'Unknown error' })
        }
    }
)

export const fetchTransactionsThunk = createAsyncThunk(
    'whatsapp/transactions/fetch',
    async (payload: TempleIdPayload, { rejectWithValue }) => {
        try {
            const response = await whatsappWalletApi.listTransactions(payload.templeId)
            return response.data
        } catch (err: any) {
            return rejectWithValue(err?.response?.data ?? { detail: err?.message ?? 'Unknown error' })
        }
    }
)

export const fetchRechargesThunk = createAsyncThunk(
    'whatsapp/recharges/fetch',
    async (payload: TempleIdPayload, { rejectWithValue }) => {
        try {
            const response = await whatsappWalletApi.listRecharges(payload.templeId)
            return response.data
        } catch (err: any) {
            return rejectWithValue(err?.response?.data ?? { detail: err?.message ?? 'Unknown error' })
        }
    }
)

export const initiateRechargeThunk = createAsyncThunk(
    'whatsapp/recharges/initiate',
    async (payload: { packageId: number } & TempleIdPayload, { rejectWithValue }) => {
        try {
            const response = await whatsappWalletApi.initiateRecharge(payload.packageId, payload.templeId)
            return response.data
        } catch (err: any) {
            return rejectWithValue(err?.response?.data ?? { detail: err?.message ?? 'Unknown error' })
        }
    }
)

export const fetchTemplatesThunk = createAsyncThunk(
    'whatsapp/templates/fetch',
    async (payload: { category?: string; language?: string } & TempleIdPayload, { rejectWithValue }) => {
        try {
            const { templeId, ...params } = payload
            const response = await whatsappTemplateApi.list(
                Object.keys(params).length > 0 ? params : undefined,
                templeId
            )
            return response.data.results || response.data || []
        } catch (err: any) {
            return rejectWithValue(err?.response?.data ?? { detail: err?.message ?? 'Unknown error' })
        }
    }
)

export const fetchPackagesThunk = createAsyncThunk(
    'whatsapp/packages/fetch',
    async (payload: TempleIdPayload, { rejectWithValue }) => {
        try {
            const response = await whatsappPackageApi.list(payload.templeId)
            return response.data
        } catch (err: any) {
            return rejectWithValue(err?.response?.data ?? { detail: err?.message ?? 'Unknown error' })
        }
    }
)

// ─── Admin Thunks (global, no temple scoping) ───────────────────────────────

export const adminFetchPackagesThunk = createAsyncThunk(
    'whatsapp/admin/packages/fetch',
    async (_, { rejectWithValue }) => {
        try {
            const response = await whatsappAdminPackageApi.list()
            return response.data
        } catch (err: any) {
            return rejectWithValue(err?.response?.data ?? { detail: err?.message ?? 'Unknown error' })
        }
    }
)

export const adminCreatePackageThunk = createAsyncThunk(
    'whatsapp/admin/packages/create',
    async (data: AdminPackageRequest, { rejectWithValue }) => {
        try {
            const response = await whatsappAdminPackageApi.create(data)
            return response.data
        } catch (err: any) {
            return rejectWithValue(err?.response?.data ?? { detail: err?.message ?? 'Unknown error' })
        }
    }
)

export const adminUpdatePackageThunk = createAsyncThunk(
    'whatsapp/admin/packages/update',
    async (payload: { id: number; data: AdminPackageRequest }, { rejectWithValue }) => {
        try {
            const response = await whatsappAdminPackageApi.update(payload.id, payload.data)
            return response.data
        } catch (err: any) {
            return rejectWithValue(err?.response?.data ?? { detail: err?.message ?? 'Unknown error' })
        }
    }
)

export const adminDeletePackageThunk = createAsyncThunk(
    'whatsapp/admin/packages/delete',
    async (id: number, { rejectWithValue }) => {
        try {
            await whatsappAdminPackageApi.delete(id)
            return id
        } catch (err: any) {
            return rejectWithValue(err?.response?.data ?? { detail: err?.message ?? 'Unknown error' })
        }
    }
)

export const adminFetchTemplatesThunk = createAsyncThunk(
    'whatsapp/admin/templates/fetch',
    async (params: AdminTemplateListParams | undefined, { rejectWithValue }) => {
        try {
            const response = await whatsappAdminTemplateApi.list(params)
            return response.data.results || response.data || []
        } catch (err: any) {
            return rejectWithValue(err?.response?.data ?? { detail: err?.message ?? 'Unknown error' })
        }
    }
)

export const adminCreateTemplateThunk = createAsyncThunk(
    'whatsapp/admin/templates/create',
    async (data: AdminTemplateRequest, { rejectWithValue }) => {
        try {
            const response = await whatsappAdminTemplateApi.create(data)
            return response.data
        } catch (err: any) {
            return rejectWithValue(err?.response?.data ?? { detail: err?.message ?? 'Unknown error' })
        }
    }
)

export const adminUpdateTemplateThunk = createAsyncThunk(
    'whatsapp/admin/templates/update',
    async (payload: { id: number; data: AdminTemplateRequest }, { rejectWithValue }) => {
        try {
            const response = await whatsappAdminTemplateApi.update(payload.id, payload.data)
            return response.data
        } catch (err: any) {
            return rejectWithValue(err?.response?.data ?? { detail: err?.message ?? 'Unknown error' })
        }
    }
)

export const adminDeleteTemplateThunk = createAsyncThunk(
    'whatsapp/admin/templates/delete',
    async (id: number, { rejectWithValue }) => {
        try {
            await whatsappAdminTemplateApi.delete(id)
            return id
        } catch (err: any) {
            return rejectWithValue(err?.response?.data ?? { detail: err?.message ?? 'Unknown error' })
        }
    }
)

// ─── Slice ───────────────────────────────────────────────────────────────────

const whatsappSlice = createSlice({
    name: 'whatsapp',
    initialState,
    reducers: {
        resetWhatsAppState() {
            return initialState
        },
        resetAdminPackageMutationApis(state) {
            state.adminPackageCreateApi = { status: apiStatus.IDLE, error: null }
            state.adminPackageUpdateApi = { status: apiStatus.IDLE, error: null }
            state.adminPackageDeleteApi = { status: apiStatus.IDLE, error: null }
        },
        resetAdminTemplateMutationApis(state) {
            state.adminTemplateCreateApi = { status: apiStatus.IDLE, error: null }
            state.adminTemplateUpdateApi = { status: apiStatus.IDLE, error: null }
            state.adminTemplateDeleteApi = { status: apiStatus.IDLE, error: null }
        },
    },
    extraReducers: (builder) => {
        // ── TAdmin: Wallet ──
        builder.addCase(fetchWalletThunk.pending, (state) => {
            state.walletApi = { status: apiStatus.LOADING, error: null }
        })
        builder.addCase(fetchWalletThunk.fulfilled, (state, action) => {
            state.walletApi = { status: apiStatus.SUCCEEDED, error: null }
            state.wallet = action.payload
        })
        builder.addCase(fetchWalletThunk.rejected, (state, action) => {
            state.walletApi = { status: apiStatus.FAILED, error: action.payload }
        })

        // ── TAdmin: Transactions ──
        builder.addCase(fetchTransactionsThunk.pending, (state) => {
            state.transactionsApi = { status: apiStatus.LOADING, error: null }
        })
        builder.addCase(fetchTransactionsThunk.fulfilled, (state, action) => {
            state.transactionsApi = { status: apiStatus.SUCCEEDED, error: null }
            transactionAdapter.setAll(state.transactions, action.payload)
        })
        builder.addCase(fetchTransactionsThunk.rejected, (state, action) => {
            state.transactionsApi = { status: apiStatus.FAILED, error: action.payload }
        })

        // ── TAdmin: Recharges ──
        builder.addCase(fetchRechargesThunk.pending, (state) => {
            state.rechargesApi = { status: apiStatus.LOADING, error: null }
        })
        builder.addCase(fetchRechargesThunk.fulfilled, (state, action) => {
            state.rechargesApi = { status: apiStatus.SUCCEEDED, error: null }
            rechargeAdapter.setAll(state.recharges, action.payload)
        })
        builder.addCase(fetchRechargesThunk.rejected, (state, action) => {
            state.rechargesApi = { status: apiStatus.FAILED, error: action.payload }
        })

        // ── TAdmin: Initiate Recharge ──
        builder.addCase(initiateRechargeThunk.pending, (state) => {
            state.rechargeInitiateApi = { status: apiStatus.LOADING, error: null }
        })
        builder.addCase(initiateRechargeThunk.fulfilled, (state) => {
            state.rechargeInitiateApi = { status: apiStatus.SUCCEEDED, error: null }
        })
        builder.addCase(initiateRechargeThunk.rejected, (state, action) => {
            state.rechargeInitiateApi = { status: apiStatus.FAILED, error: action.payload }
        })

        // ── TAdmin: Templates ──
        builder.addCase(fetchTemplatesThunk.pending, (state) => {
            state.templatesApi = { status: apiStatus.LOADING, error: null }
        })
        builder.addCase(fetchTemplatesThunk.fulfilled, (state, action) => {
            state.templatesApi = { status: apiStatus.SUCCEEDED, error: null }
            templateAdapter.setAll(state.templates, action.payload)
        })
        builder.addCase(fetchTemplatesThunk.rejected, (state, action) => {
            state.templatesApi = { status: apiStatus.FAILED, error: action.payload }
        })

        // ── TAdmin: Packages ──
        builder.addCase(fetchPackagesThunk.pending, (state) => {
            state.packagesApi = { status: apiStatus.LOADING, error: null }
        })
        builder.addCase(fetchPackagesThunk.fulfilled, (state, action) => {
            state.packagesApi = { status: apiStatus.SUCCEEDED, error: null }
            packageAdapter.setAll(state.packages, action.payload)
        })
        builder.addCase(fetchPackagesThunk.rejected, (state, action) => {
            state.packagesApi = { status: apiStatus.FAILED, error: action.payload }
        })

        // ── Admin: Packages List ──
        builder.addCase(adminFetchPackagesThunk.pending, (state) => {
            state.adminPackagesApi = { status: apiStatus.LOADING, error: null }
        })
        builder.addCase(adminFetchPackagesThunk.fulfilled, (state, action) => {
            state.adminPackagesApi = { status: apiStatus.SUCCEEDED, error: null }
            adminPackageAdapter.setAll(state.adminPackages, action.payload)
        })
        builder.addCase(adminFetchPackagesThunk.rejected, (state, action) => {
            state.adminPackagesApi = { status: apiStatus.FAILED, error: action.payload }
        })

        // ── Admin: Package Create ──
        builder.addCase(adminCreatePackageThunk.pending, (state) => {
            state.adminPackageCreateApi = { status: apiStatus.LOADING, error: null }
        })
        builder.addCase(adminCreatePackageThunk.fulfilled, (state, action) => {
            state.adminPackageCreateApi = { status: apiStatus.SUCCEEDED, error: null }
            adminPackageAdapter.addOne(state.adminPackages, action.payload)
        })
        builder.addCase(adminCreatePackageThunk.rejected, (state, action) => {
            state.adminPackageCreateApi = { status: apiStatus.FAILED, error: action.payload }
        })

        // ── Admin: Package Update ──
        builder.addCase(adminUpdatePackageThunk.pending, (state) => {
            state.adminPackageUpdateApi = { status: apiStatus.LOADING, error: null }
        })
        builder.addCase(adminUpdatePackageThunk.fulfilled, (state, action) => {
            state.adminPackageUpdateApi = { status: apiStatus.SUCCEEDED, error: null }
            adminPackageAdapter.upsertOne(state.adminPackages, action.payload)
        })
        builder.addCase(adminUpdatePackageThunk.rejected, (state, action) => {
            state.adminPackageUpdateApi = { status: apiStatus.FAILED, error: action.payload }
        })

        // ── Admin: Package Delete ──
        builder.addCase(adminDeletePackageThunk.pending, (state) => {
            state.adminPackageDeleteApi = { status: apiStatus.LOADING, error: null }
        })
        builder.addCase(adminDeletePackageThunk.fulfilled, (state, action) => {
            state.adminPackageDeleteApi = { status: apiStatus.SUCCEEDED, error: null }
            adminPackageAdapter.removeOne(state.adminPackages, action.payload)
        })
        builder.addCase(adminDeletePackageThunk.rejected, (state, action) => {
            state.adminPackageDeleteApi = { status: apiStatus.FAILED, error: action.payload }
        })

        // ── Admin: Templates List ──
        builder.addCase(adminFetchTemplatesThunk.pending, (state) => {
            state.adminTemplatesApi = { status: apiStatus.LOADING, error: null }
        })
        builder.addCase(adminFetchTemplatesThunk.fulfilled, (state, action) => {
            state.adminTemplatesApi = { status: apiStatus.SUCCEEDED, error: null }
            adminTemplateAdapter.setAll(state.adminTemplates, action.payload)
        })
        builder.addCase(adminFetchTemplatesThunk.rejected, (state, action) => {
            state.adminTemplatesApi = { status: apiStatus.FAILED, error: action.payload }
        })

        // ── Admin: Template Create ──
        builder.addCase(adminCreateTemplateThunk.pending, (state) => {
            state.adminTemplateCreateApi = { status: apiStatus.LOADING, error: null }
        })
        builder.addCase(adminCreateTemplateThunk.fulfilled, (state, action) => {
            state.adminTemplateCreateApi = { status: apiStatus.SUCCEEDED, error: null }
            adminTemplateAdapter.addOne(state.adminTemplates, action.payload)
        })
        builder.addCase(adminCreateTemplateThunk.rejected, (state, action) => {
            state.adminTemplateCreateApi = { status: apiStatus.FAILED, error: action.payload }
        })

        // ── Admin: Template Update ──
        builder.addCase(adminUpdateTemplateThunk.pending, (state) => {
            state.adminTemplateUpdateApi = { status: apiStatus.LOADING, error: null }
        })
        builder.addCase(adminUpdateTemplateThunk.fulfilled, (state, action) => {
            state.adminTemplateUpdateApi = { status: apiStatus.SUCCEEDED, error: null }
            adminTemplateAdapter.upsertOne(state.adminTemplates, action.payload)
        })
        builder.addCase(adminUpdateTemplateThunk.rejected, (state, action) => {
            state.adminTemplateUpdateApi = { status: apiStatus.FAILED, error: action.payload }
        })

        // ── Admin: Template Delete ──
        builder.addCase(adminDeleteTemplateThunk.pending, (state) => {
            state.adminTemplateDeleteApi = { status: apiStatus.LOADING, error: null }
        })
        builder.addCase(adminDeleteTemplateThunk.fulfilled, (state, action) => {
            state.adminTemplateDeleteApi = { status: apiStatus.SUCCEEDED, error: null }
            adminTemplateAdapter.removeOne(state.adminTemplates, action.payload)
        })
        builder.addCase(adminDeleteTemplateThunk.rejected, (state, action) => {
            state.adminTemplateDeleteApi = { status: apiStatus.FAILED, error: action.payload }
        })
    },
})

export default whatsappSlice.reducer
export const { resetWhatsAppState, resetAdminPackageMutationApis, resetAdminTemplateMutationApis } = whatsappSlice.actions

// ─── TAdmin Selectors ────────────────────────────────────────────────────────

export const selectWhatsAppWallet = (state: AppState) => state.whatsapp.wallet
export const selectWhatsAppWalletApi = (state: AppState) => state.whatsapp.walletApi
export const selectWhatsAppTransactionsApi = (state: AppState) => state.whatsapp.transactionsApi
export const selectWhatsAppRechargesApi = (state: AppState) => state.whatsapp.rechargesApi
export const selectWhatsAppRechargeInitiateApi = (state: AppState) => state.whatsapp.rechargeInitiateApi
export const selectWhatsAppTemplatesApi = (state: AppState) => state.whatsapp.templatesApi
export const selectWhatsAppPackagesApi = (state: AppState) => state.whatsapp.packagesApi

export const {
    selectAll: selectAllTransactions,
} = transactionAdapter.getSelectors((state: AppState) => state.whatsapp.transactions)

export const {
    selectAll: selectAllRecharges,
} = rechargeAdapter.getSelectors((state: AppState) => state.whatsapp.recharges)

export const {
    selectAll: selectAllTemplates,
} = templateAdapter.getSelectors((state: AppState) => state.whatsapp.templates)

export const {
    selectAll: selectAllPackages,
} = packageAdapter.getSelectors((state: AppState) => state.whatsapp.packages)

// ─── Admin Selectors ─────────────────────────────────────────────────────────

export const selectAdminPackagesApi = (state: AppState) => state.whatsapp.adminPackagesApi
export const selectAdminPackageCreateApi = (state: AppState) => state.whatsapp.adminPackageCreateApi
export const selectAdminPackageUpdateApi = (state: AppState) => state.whatsapp.adminPackageUpdateApi
export const selectAdminPackageDeleteApi = (state: AppState) => state.whatsapp.adminPackageDeleteApi
export const selectAdminTemplatesApi = (state: AppState) => state.whatsapp.adminTemplatesApi
export const selectAdminTemplateCreateApi = (state: AppState) => state.whatsapp.adminTemplateCreateApi
export const selectAdminTemplateUpdateApi = (state: AppState) => state.whatsapp.adminTemplateUpdateApi
export const selectAdminTemplateDeleteApi = (state: AppState) => state.whatsapp.adminTemplateDeleteApi

export const {
    selectAll: selectAllAdminPackages,
    selectById: selectAdminPackageById,
} = adminPackageAdapter.getSelectors((state: AppState) => state.whatsapp.adminPackages)

export const {
    selectAll: selectAllAdminTemplates,
    selectById: selectAdminTemplateById,
} = adminTemplateAdapter.getSelectors((state: AppState) => state.whatsapp.adminTemplates)
